import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import type { BranchData } from './types';
import { getBranchHealth } from './types';

const copy = {
  en: { nudge: 'Some branches need a little sunlight.', detail: (name: string) => `You haven't reached out to ${name} in a while.`, cta: 'Send a message' },
  es: { nudge: 'Algunas ramas necesitan un poco de sol.', detail: (name: string) => `No te has comunicado con ${name} en un tiempo.`, cta: 'Enviar un mensaje' },
  fr: { nudge: 'Certaines branches ont besoin d\'un peu de soleil.', detail: (name: string) => `Vous n'avez pas contacté ${name} depuis un moment.`, cta: 'Envoyer un message' },
  pt: { nudge: 'Alguns galhos precisam de um pouco de sol.', detail: (name: string) => `Você não entrou em contato com ${name} há um tempo.`, cta: 'Enviar uma mensagem' },
  ar: { nudge: 'بعض الفروع تحتاج قليلاً من الضوء.', detail: (name: string) => `لم تتواصل مع ${name} منذ فترة.`, cta: 'أرسل رسالة' },
  ja: { nudge: '日差しが必要な枝があります。', detail: (name: string) => `${name}にしばらく連絡していません。`, cta: 'メッセージを送る' },
  ko: { nudge: '햇빛이 필요한 가지가 있습니다.', detail: (name: string) => `${name}에게 한동안 연락하지 않았습니다.`, cta: '메시지 보내기' },
  id: { nudge: 'Beberapa cabang butuh sedikit sinar matahari.', detail: (name: string) => `Anda belum menghubungi ${name} belakangan ini.`, cta: 'Kirim pesan' },
  hi: { nudge: 'कुछ शाखाओं को थोड़ी धूप चाहिए।', detail: (name: string) => `आपने ${name} से काफ़ी समय से संपर्क नहीं किया।`, cta: 'संदेश भेजें' },
};

interface BranchNudgeProps {
  branches: BranchData[];
}

export function BranchNudge({ branches }: BranchNudgeProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const dormantBranches = branches.filter((b) => getBranchHealth(b.lastSentAt) === 'dormant');

  if (dormantBranches.length === 0) return null;

  const nudgeName = dormantBranches[0].recipientName;

  return (
    <AnimatePresence>
      <motion.div
        className="rounded-2xl p-4 flex items-center gap-4"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 160, 60, 0.08), rgba(255, 200, 80, 0.04))',
          border: '1px solid rgba(255, 200, 80, 0.12)',
        }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
      >
        <Sun className="h-5 w-5 text-amber-400 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-white/80">
            {t.nudge}
          </p>
          <p className="text-xs text-white/50 mt-0.5">
            {t.detail(nudgeName)}
          </p>
        </div>
        <Link to="/create">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 rounded-xl border-amber-500/30 text-amber-300 hover:bg-amber-500/10 text-xs"
          >
            <Send className="h-3.5 w-3.5" />
            {t.cta}
          </Button>
        </Link>
      </motion.div>
    </AnimatePresence>
  );
}
